// Activity Logging Service for GoIcon API
import { CorsService } from './corsService';
import { JWTService } from './jwtService';

export interface ActivityLogRequest {
  LogCategory: string[]; // Array of dimension/category names
  LogDetail: string; // Activity description
  ResidentId: number; // Resident ID (locationId)
  ExternalId: number; // External ID from JWT
}

export class ActivityService {
  /**
   * Get the GoIcon activity logging API URL
   */
  private static getApiUrl(envOverride?: 'sandbox' | 'production'): string {
    const withEnv = (baseUrl: string) => {
      if (!envOverride) return baseUrl;
      return baseUrl.includes('?')
        ? `${baseUrl}&env=${encodeURIComponent(envOverride)}`
        : `${baseUrl}?env=${encodeURIComponent(envOverride)}`;
    };

    // Check if we have a proxy server configured
    const proxyUrl = process.env.REACT_APP_PROXY_URL;
    if (proxyUrl && proxyUrl !== 'https://wellness.goicon.co') {
      return withEnv(`${proxyUrl}/api/goicon-activity`);
    }
    
    // In development or Netlify, use Netlify Functions as proxy
    if (process.env.NODE_ENV === 'development' || 
        window.location.hostname.includes('netlify.app') || 
        window.location.hostname.includes('goicon.co')) {
      // Prefer explicit functions base when provided
      const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
      if (explicitBase) {
        return withEnv(`${explicitBase.replace(/\/$/, '')}/goicon-activity-proxy`);
      }
      return withEnv('/.netlify/functions/goicon-activity-proxy');
    }
    
    // Fallback: direct API call (may have CORS issues)
    console.warn('⚠️ Using direct GoIcon API (may have CORS issues)');
    const useSandbox = envOverride === 'sandbox';
    return useSandbox
      ? 'https://sboxall.peoplepowerco.com/espapi/integration/goicon/logActivity'
      : 'https://app.caredaily.ai/espapi/integration/goicon/logActivity';
  }

  /**
   * Log an activity to the GoIcon API
   */
  static async logActivity(
    activityData: ActivityLogRequest,
    options?: { envOverride?: 'sandbox' | 'production' }
  ): Promise<{ success: boolean; message?: string }> {
    const envOverride = options?.envOverride;
    const apiUrl = this.getApiUrl(envOverride);
    
    console.log('📝 Logging activity to CareDaily API:');
    console.log('  ResidentId:', activityData.ResidentId);
    console.log('  ExternalId:', activityData.ExternalId);
    console.log('  Categories:', activityData.LogCategory.join(', '));
    console.log('  Detail:', activityData.LogDetail.substring(0, 100) + (activityData.LogDetail.length > 100 ? '...' : ''));
    console.log('📡 API URL:', apiUrl);

    try {
      // The goicon-activity-proxy verifies this token and resolves the facility's
      // ESP key server-side; without it the proxy returns 401.
      const token = JWTService.getToken();
      const response = await CorsService.corsEnabledFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(envOverride ? { 'X-CareDaily-Env': envOverride } : {}),
        },
        body: JSON.stringify(activityData),
      });

      console.log('📡 HTTP Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ Activity logging failed (HTTP', response.status + '):', errorData);
        console.error('❌ Error details:', JSON.stringify(errorData, null, 2));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to log activity`);
      }

      const result = await response.json();
      
      console.log('✅ Activity logged successfully to CareDaily API');
      console.log('📦 API Response:', result);
      console.log('🌍 Environment:', result.env || 'unknown');
      console.log('🔗 API URL:', result.apiUrl || 'unknown');
      console.log('⏰ Timestamp:', result.timestamp || 'unknown');
      
      if (result.data) {
        console.log('📝 Response data:', result.data);
      }

      return {
        success: true,
        message: 'Activity logged successfully'
      };
    } catch (error) {
      console.error('❌ Error logging activity:', error);
      throw error;
    }
  }

  /**
   * Validate activity data before sending
   */
  static validateActivityData(data: Partial<ActivityLogRequest>): string | null {
    if (!data.LogDetail || data.LogDetail.trim().length === 0) {
      return 'Activity description is required';
    }

    if (!data.LogCategory || data.LogCategory.length === 0) {
      return 'At least one wellness dimension must be selected';
    }

    if (!data.ResidentId) {
      return 'Resident ID is required';
    }

    if (!data.ExternalId) {
      return 'External ID is required';
    }

    if (data.LogDetail.length > 500) {
      return 'Activity description must be 500 characters or less';
    }

    return null;
  }

}



